import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  useCreateEmailConnection,
  useUpdateEmailConnection,
} from "@/hooks/use-email-connections";

import {
  defaultEmailConnectionValues,
  emailConnectionSchema,
  type EmailConnectionFormValues,
} from "./email-connection-schema";

import type { EmailConnection } from "@artisancode/api-types";

export function useEmailConnectionForm(
  open: boolean,
  editing: EmailConnection | null,
  onSuccess: () => void,
) {
  const { mutate: create, isPending: isCreating } = useCreateEmailConnection();
  const { mutate: update, isPending: isUpdating } = useUpdateEmailConnection();

  const form = useForm<EmailConnectionFormValues>({
    resolver: zodResolver(emailConnectionSchema),
    defaultValues: defaultEmailConnectionValues,
  });

  useEffect(() => {
    if (!open) return;
    if (!editing) {
      form.reset(defaultEmailConnectionValues);
      return;
    }
    form.reset({
      name: editing.name,
      fromAddress: editing.fromAddress,
      smtpHost: editing.smtpHost ?? "",
      smtpPort: String(editing.smtpPort ?? 587),
      smtpUsername: editing.smtpUsername ?? "",
      smtpPassword: "",
      smtpSecure: !!editing.smtpSecure,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  function onSubmit(values: EmailConnectionFormValues) {
    const payload = {
      name: values.name,
      fromAddress: values.fromAddress.trim(),
      smtpHost: values.smtpHost.trim(),
      smtpPort: Number(values.smtpPort),
      smtpUsername: values.smtpUsername.trim(),
      smtpPassword: values.smtpPassword?.trim()
        ? values.smtpPassword.trim()
        : undefined,
      smtpSecure: values.smtpSecure,
    };

    if (editing) {
      update(
        { id: editing.id, ...payload },
        {
          onSuccess: () => {
            toast.success("Koneksi email berhasil diperbarui.");
            onSuccess();
          },
        },
      );
    } else {
      create(
        { ...payload, smtpPassword: payload.smtpPassword ?? "" },
        {
          onSuccess: () => {
            toast.success("Koneksi email berhasil ditambahkan.");
            onSuccess();
          },
        },
      );
    }
  }

  return {
    form,
    isSubmitting: isCreating || isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
